import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Phone, Globe, Clock, Loader2 } from 'lucide-react';
import { DynamicIcon, colorClasses } from '../ui/DynamicIcon';
import { SearchableBoundaryInput } from './SearchableBoundaryInput';
import { SearchableRoadMultiSelect } from './SearchableRoadMultiSelect';
import { MotorwayLinkStatusChip } from './MotorwayLinkStatusChip';
import {
  TurnRestrictionSurveyFields,
  type TurnRestrictionRoadPick,
  type TurnRestrictionTypeId,
} from './TurnRestrictionSurveyFields';
import type { JunctionExitRoad } from '../../utils/junctionExitRoads';
import { OpeningHoursEditor } from '../shared/OpeningHoursEditor';
import { ghanaPostGpsAdminCodeLabel } from '../../utils/adminBoundaryFocus';
import { isGssBoundaryPlaceCategory, contributionFeatureShowsBusinessDetails } from '../../utils/contributionPlaceAdmin';
import {
  GHANA_POST_GPS_FULL_REGEX,
  GHANA_POST_GPS_MAX_LENGTH,
  sanitizeGhanaPostGpsInput,
} from '../../utils/ghanaPostGps';
import type { AdminBoundaryInfo } from '../../services/api';
import { imageFilesFromClipboard, isContributionImageFile } from '../../utils/contributionPhotos';
import { RouteTagsInput } from './RouteTagsInput';
import {
  buildLaneDirectionsFromCounts,
  expectedManualLaneCount,
  LANE_DIRECTION_OPTIONS,
  resizeLaneDirections,
  resizeLaneWidthStrings,
  isMultiCarriageway,
  validateRoadFlowFields,
  type LaneDirection,
} from './roadFlowUtils';
import { FieldHint, ROAD_FLOW_HINTS, TRAFFIC_FLOW_DIRECTIONS_INTRO } from './FieldHint';
import {
  STRIP_CAPTURE_MODE_OPTIONS,
  captureModesForLayout,
  type StripCaptureMode,
} from '../../utils/roadStripSurvey';
import {
  ACCESS_TAG_OPTIONS,
  CARRIAGEWAY_SIDE_PLACEMENT_FIELDS,
  DIVIDED_KERB_PLACEMENT_OPTIONS,
  FACILITY_DIMENSION_FIELDS,
  GUTTER_OPTIONS,
  SIDE_PLACEMENT_OPTIONS,
  SIDE_FACILITY_CONDITION_OPTIONS,
  carriagewaySideFieldKey,
  facilityDimensionsForScope,
  hasActiveFacilityPlacement,
  placementForScope,
  segmentMapForScope,
  supportsSideFacilityTags,
  validateSideFacilityFields,
  type AccessTag,
  type GeometryFacilityKey,
  type GutterTag,
  type SideFacilityFormState,
  type SideFacilitySegmentMap,
  type SidePlacement,
  type StripKerbPlacement,
  type SideFacilityCondition,
} from '../../utils/roadSideFacilities';
import { formatContactPhoneForInput, validateContactPhone } from '../../utils/contactPhone';
import { TRANSIT_STOP_FACILITIES } from '../../utils/transitStopFacilities';
import {
  RoadDetailsAccordionSection,
  RoadGeometryStatusBar,
  type AccordionBadge,
} from './RoadDetailsAccordion';

const MAX_CONTRIBUTION_PHOTOS = 3;
const GPS_REGEX = GHANA_POST_GPS_FULL_REGEX;

export interface ContributionCategoryMeta {
  id: string;
  name: string;
  icon: string;
  color: string;
}

export interface ContributionFeatureType {
  id: string;
  name: string;
}

export interface ContributionDetailsFormProps {
  // ── Identity ──────────────────────────────────────────────────────────────
  featureType: ContributionFeatureType | null;
  selectedCategory: string | null;
  categoryMeta: ContributionCategoryMeta | null;
  name: string;
  onNameChange: (v: string) => void;
  onChangeCategoryStep: () => void;

  // ── Address ───────────────────────────────────────────────────────────────
  addrRegion: string;
  addrDistrict: string;
  addrCity: string;
  addrStreet: string;
  addrSuburb: string;
  /** Building house number — shown read-only beside street when lockHouseNumber is set. */
  houseNumber?: string;
  lockHouseNumber?: boolean;
  ghanaPostGps: string;
  onAddrRegionChange: (name: string, meta?: { id?: string }) => void;
  onAddrDistrictChange: (name: string, meta?: { id?: string }) => void;
  onAddrCityChange: (name: string) => void;
  onAddrStreetChange: (v: string) => void;
  onAddrSuburbChange: (name: string) => void;
  onGhanaPostGpsChange: (v: string) => void;
  fetchRegions: (q: string) => Promise<AdminBoundaryInfo[]>;
  fetchDistricts: (q: string) => Promise<AdminBoundaryInfo[]>;
  fetchTowns: (q: string) => Promise<AdminBoundaryInfo[]>;
  fetchSuburbs: (q: string) => Promise<AdminBoundaryInfo[]>;
  addressLine: string;
  addressLookupStatus: 'idle' | 'loading' | 'ok' | 'error';
  /** Fields filled via nearest-in-district fallback — user should confirm. */
  suggestedAddressFields?: string[];

  landmarkSourceGroup?: string | null;

  // ── Contact & online ──────────────────────────────────────────────────────
  phone: string; onPhoneChange: (v: string) => void;
  mobile: string; onMobileChange: (v: string) => void;
  whatsapp: string; onWhatsappChange: (v: string) => void;
  contactEmail: string; onContactEmailChange: (v: string) => void;
  website: string; onWebsiteChange: (v: string) => void;
  facebook: string; onFacebookChange: (v: string) => void;
  instagram: string; onInstagramChange: (v: string) => void;
  openingHours: string; onOpeningHoursChange: (v: string) => void;

  // ── Road-specific ─────────────────────────────────────────────────────────
  roadSurface: string; onRoadSurfaceChange: (v: string) => void;
  roadSmoothness: string; onRoadSmoothnessChange: (v: string) => void;
  roadCarriagewayCount: number | null; onRoadCarriagewayCountChange: (v: number) => void;
  roadLanes: string; onRoadLanesChange: (v: string) => void;
  roadLanesForward: string; onRoadLanesForwardChange: (v: string) => void;
  roadLanesBackward: string; onRoadLanesBackwardChange: (v: string) => void;
  roadLaneDirections: LaneDirection[];
  onRoadLaneDirectionsChange: (v: LaneDirection[] | ((prev: LaneDirection[]) => LaneDirection[])) => void;
  roadManualLaneDirections: boolean; onRoadManualLaneDirectionsChange: (v: boolean) => void;
  roadUsePerLaneWidths?: boolean;
  onRoadUsePerLaneWidthsChange?: (v: boolean) => void;
  roadLaneWidthsM?: string[];
  onRoadLaneWidthsMChange?: (v: string[]) => void;
  roadCarriagewayALaneWidthsM?: string[];
  onRoadCarriagewayALaneWidthsMChange?: (v: string[]) => void;
  roadCarriagewayBLaneWidthsM?: string[];
  onRoadCarriagewayBLaneWidthsMChange?: (v: string[]) => void;
  roadToll: boolean; onRoadTollChange: (v: boolean) => void;
  roadFloodProne: boolean; onRoadFloodProneChange: (v: boolean) => void;
  roadPotholes: boolean; onRoadPotholesChange: (v: boolean) => void;
  roadStreetVendors: boolean; onRoadStreetVendorsChange: (v: boolean) => void;
  roadAccidentProne: boolean; onRoadAccidentProneChange: (v: boolean) => void;
  roadHawkers: boolean; onRoadHawkersChange: (v: boolean) => void;
  roadLit: boolean; onRoadLitChange: (v: boolean) => void;
  roadDrainage: boolean; onRoadDrainageChange: (v: boolean) => void;
  roadMaxspeed: string; onRoadMaxspeedChange: (v: string) => void;
  roadCrossingType: string; onRoadCrossingTypeChange: (v: string) => void;
  roadWidth: string; onRoadWidthChange: (v: string) => void;
  roadCarriagewayAWidth: string; onRoadCarriagewayAWidthChange: (v: string) => void;
  roadCarriagewayBWidth: string; onRoadCarriagewayBWidthChange: (v: string) => void;
  singleStripCaptureMode: StripCaptureMode;
  onSingleStripCaptureModeChange: (v: StripCaptureMode) => void;
  stripACaptureMode: StripCaptureMode;
  onStripACaptureModeChange: (v: StripCaptureMode) => void;
  stripBCaptureMode: StripCaptureMode;
  onStripBCaptureModeChange: (v: StripCaptureMode) => void;
  divideWidthM: string;
  onDivideWidthMChange: (v: string) => void;
  dividedDivideReady: boolean;
  dividedCarriagewayAReady: boolean;
  dividedCarriagewayBReady: boolean;
  dividedCarriagewayReady: boolean;
  stripATraceReady: boolean;
  stripBTraceReady: boolean;
  singleStripTraceReady: boolean;
  onStartMarkingDivide?: () => void;
  onStartMarkingStripA?: () => void;
  onStartMarkingStripB?: () => void;
  onStartMarkingSingleStrip?: () => void;
  /** Shown once after switching from single → divided when the prior trace was cleared. */
  dividedSurveySetupHint?: boolean;
  onDismissDividedSurveySetupHint?: () => void;
  /** Policy §2.14 — layout/capture chosen before map; accordions in Details. */
  roadGuidedSurveyFlow?: boolean;
  roadLayoutSubtitle?: string;
  roadGeometryStatusRows?: { label: string; statusLabel: string }[];
  onEditRoadGeometry?: () => void;
  roadCarriagewayALaneDirections: LaneDirection[];
  onRoadCarriagewayALaneDirectionsChange: (v: LaneDirection[] | ((prev: LaneDirection[]) => LaneDirection[])) => void;
  roadCarriagewayBLaneDirections: LaneDirection[];
  onRoadCarriagewayBLaneDirectionsChange: (v: LaneDirection[] | ((prev: LaneDirection[]) => LaneDirection[])) => void;
  roadSideFacilities: SideFacilityFormState;
  onRoadSideFacilitiesChange: (patch: Partial<SideFacilityFormState>) => void;

  // ── Tree-specific ───────────────────────────────────────────────────────────
  treeHeight: string;
  onTreeHeightChange: (v: string) => void;

  // ── Aeroway-specific ────────────────────────────────────────────────────────
  aerowayRef: string;
  onAerowayRefChange: (v: string) => void;
  aerowaySurface: string;
  onAerowaySurfaceChange: (v: string) => void;
  aerowayWidth: string;
  onAerowayWidthChange: (v: string) => void;

  // ── Motorway junction-specific ──────────────────────────────────────────────
  junctionRef: string;
  onJunctionRefChange: (v: string) => void;
  junctionExitRoads: JunctionExitRoad[];
  onJunctionExitRoadsChange: (roads: JunctionExitRoad[]) => void;
  junctionSearchProximity?: { lat: number; lng: number } | null;

  // ── Turn restriction (E3) ───────────────────────────────────────────────────
  turnFromRoad?: TurnRestrictionRoadPick | null;
  onTurnFromRoadChange?: (road: TurnRestrictionRoadPick | null) => void;
  turnToRoad?: TurnRestrictionRoadPick | null;
  onTurnToRoadChange?: (road: TurnRestrictionRoadPick | null) => void;
  turnRestrictionType?: TurnRestrictionTypeId | '';
  onTurnRestrictionTypeChange?: (id: TurnRestrictionTypeId) => void;
  turnSearchProximity?: { lat: number; lng: number } | null;

  // ── Transport stop-specific ───────────────────────────────────────────────
  stopRef: string;
  onStopRefChange: (v: string) => void;
  stopRoutes: string[];
  onStopRoutesChange: (v: string[]) => void;
  stopOperators: string[];
  onStopOperatorsChange: (v: string[]) => void;
  stopShelter: boolean;
  onStopShelterChange: (v: boolean) => void;
  stopBench: boolean;
  onStopBenchChange: (v: boolean) => void;
  stopLighting: boolean;
  onStopLightingChange: (v: boolean) => void;

  // ── POI business extras (optional) ───────────────────────────────────────
  poiCuisine: string;
  onPoiCuisineChange: (v: string) => void;
  poiBrand: string;
  onPoiBrandChange: (v: string) => void;
  poiWheelchair: string;
  onPoiWheelchairChange: (v: string) => void;
  paymentMobileMoney: boolean;
  onPaymentMobileMoneyChange: (v: boolean) => void;
  paymentCard: boolean;
  onPaymentCardChange: (v: boolean) => void;
  mobileMoneyProvider: string;
  onMobileMoneyProviderChange: (v: string) => void;

  // ── Media & description ───────────────────────────────────────────────────
  description: string; onDescriptionChange: (v: string) => void;
  descriptionSourceUrl: string; onDescriptionSourceUrlChange: (v: string) => void;
  photoDataUrls: string[];
  onPhotoInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhotoDrop: (files: File[]) => void;
  onRemovePhoto: (index: number) => void;

  // ── Display ───────────────────────────────────────────────────────────────
  /**
   * When true the full Address section (Street, Suburb, Ghana PostGPS),
   * Contact, Online Presence and Opening Hours sections are shown.
   * Set to true for poi/building/landmark feature types and always when editing.
   */
  isFullDetails: boolean;
  /** Show Area/Suburb for transport, roads, trees, etc. (not only POI/building). */
  showLocationSuburb?: boolean;
  /** Retry reverse geocode when lookup failed. */
  onRetryAddressLookup?: () => void;

  /** Override primary action label (e.g. "Review & Submit Update"). */
  submitLabel?: string;
  submitLoading?: boolean;

  /** Official area (GSS boundary): description and/or photos only — no name/address/contact. */
  metadataOnly?: boolean;
  metadataUpdateIntent?: 'description' | 'photos' | 'both';
  /** Read-only boundary label when metadataOnly (replaces name field). */
  officialBoundaryLabel?: string;

  // ── Actions ───────────────────────────────────────────────────────────────
  onSubmit: () => void;
}

const inputClass =
  'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-gray-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder-gray-400 dark:placeholder-neutral-500';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1';

export function ContributionDetailsForm({
  featureType,
  selectedCategory,
  categoryMeta,
  name,
  onNameChange,
  onChangeCategoryStep,
  addrRegion,
  addrDistrict,
  addrCity,
  addrStreet,
  addrSuburb,
  houseNumber = '',
  lockHouseNumber = false,
  ghanaPostGps,
  onAddrRegionChange,
  onAddrDistrictChange,
  onAddrCityChange,
  onAddrStreetChange,
  onAddrSuburbChange,
  onGhanaPostGpsChange,
  fetchRegions,
  fetchDistricts,
  fetchTowns,
  fetchSuburbs,
  addressLine,
  addressLookupStatus,
  suggestedAddressFields = [],
  landmarkSourceGroup = null,
  phone, onPhoneChange,
  mobile, onMobileChange,
  whatsapp, onWhatsappChange,
  contactEmail, onContactEmailChange,
  website, onWebsiteChange,
  facebook, onFacebookChange,
  instagram, onInstagramChange,
  openingHours, onOpeningHoursChange,
  roadSurface, onRoadSurfaceChange,
  roadSmoothness, onRoadSmoothnessChange,
  roadCarriagewayCount, onRoadCarriagewayCountChange,
  roadLanes, onRoadLanesChange,
  roadLanesForward, onRoadLanesForwardChange,
  roadLanesBackward, onRoadLanesBackwardChange,
  roadLaneDirections, onRoadLaneDirectionsChange,
  roadManualLaneDirections, onRoadManualLaneDirectionsChange,
  roadUsePerLaneWidths = false,
  onRoadUsePerLaneWidthsChange,
  roadLaneWidthsM = [],
  onRoadLaneWidthsMChange,
  roadCarriagewayALaneWidthsM = [],
  onRoadCarriagewayALaneWidthsMChange,
  roadCarriagewayBLaneWidthsM = [],
  onRoadCarriagewayBLaneWidthsMChange,
  roadToll, onRoadTollChange,
  roadFloodProne, onRoadFloodProneChange,
  roadPotholes, onRoadPotholesChange,
  roadStreetVendors, onRoadStreetVendorsChange,
  roadAccidentProne, onRoadAccidentProneChange,
  roadHawkers, onRoadHawkersChange,
  roadLit, onRoadLitChange,
  roadDrainage, onRoadDrainageChange,
  roadMaxspeed, onRoadMaxspeedChange,
  roadCrossingType, onRoadCrossingTypeChange,
  roadWidth, onRoadWidthChange,
  roadCarriagewayAWidth, onRoadCarriagewayAWidthChange,
  roadCarriagewayBWidth, onRoadCarriagewayBWidthChange,
  singleStripCaptureMode,
  onSingleStripCaptureModeChange,
  stripACaptureMode,
  onStripACaptureModeChange,
  stripBCaptureMode,
  onStripBCaptureModeChange,
  divideWidthM,
  onDivideWidthMChange,
  dividedDivideReady,
  dividedCarriagewayAReady,
  dividedCarriagewayBReady,
  dividedCarriagewayReady,
  stripATraceReady,
  stripBTraceReady,
  singleStripTraceReady,
  onStartMarkingDivide,
  onStartMarkingStripA,
  onStartMarkingStripB,
  onStartMarkingSingleStrip,
  dividedSurveySetupHint = false,
  onDismissDividedSurveySetupHint,
  roadGuidedSurveyFlow = false,
  roadLayoutSubtitle = '',
  roadGeometryStatusRows = [],
  onEditRoadGeometry,
  roadCarriagewayALaneDirections,
  onRoadCarriagewayALaneDirectionsChange,
  roadCarriagewayBLaneDirections,
  onRoadCarriagewayBLaneDirectionsChange,
  roadSideFacilities,
  onRoadSideFacilitiesChange,
  treeHeight, onTreeHeightChange,
  aerowayRef, onAerowayRefChange,
  aerowaySurface, onAerowaySurfaceChange,
  aerowayWidth, onAerowayWidthChange,
  junctionRef, onJunctionRefChange,
  junctionExitRoads, onJunctionExitRoadsChange,
  junctionSearchProximity = null,
  turnFromRoad = null,
  onTurnFromRoadChange,
  turnToRoad = null,
  onTurnToRoadChange,
  turnRestrictionType = '',
  onTurnRestrictionTypeChange,
  turnSearchProximity = null,
  stopRef, onStopRefChange,
  stopRoutes, onStopRoutesChange,
  stopOperators, onStopOperatorsChange,
  stopShelter, onStopShelterChange,
  stopBench, onStopBenchChange,
  stopLighting, onStopLightingChange,
  poiCuisine, onPoiCuisineChange,
  poiBrand, onPoiBrandChange,
  poiWheelchair, onPoiWheelchairChange,
  paymentMobileMoney, onPaymentMobileMoneyChange,
  paymentCard, onPaymentCardChange,
  mobileMoneyProvider, onMobileMoneyProviderChange,
  description, onDescriptionChange,
  descriptionSourceUrl, onDescriptionSourceUrlChange,
  photoDataUrls,
  onPhotoInputChange,
  onPhotoDrop,
  onRemovePhoto,
  isFullDetails,
  showLocationSuburb = false,
  onRetryAddressLookup,
  submitLabel,
  submitLoading = false,
  metadataOnly = false,
  metadataUpdateIntent = 'both',
  officialBoundaryLabel,
  onSubmit,
}: ContributionDetailsFormProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPasteReady, setIsPasteReady] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);

  const handlePhoneChange = (value: string) => {
    onPhoneChange(formatContactPhoneForInput(value));
    if (phoneError) setPhoneError(validateContactPhone(value, 'Phone'));
  };
  const handleMobileChange = (value: string) => {
    onMobileChange(formatContactPhoneForInput(value));
    if (mobileError) setMobileError(validateContactPhone(value, 'Mobile'));
  };
  const handleWhatsappChange = (value: string) => {
    onWhatsappChange(formatContactPhoneForInput(value));
    if (whatsappError) setWhatsappError(validateContactPhone(value, 'WhatsApp'));
  };

  const atMax = photoDataUrls.length >= MAX_CONTRIBUTION_PHOTOS;

  const ingestClipboardImages = useCallback((clipboard: DataTransfer | null) => {
    if (atMax || !clipboard) return false;
    const files = imageFilesFromClipboard(clipboard);
    if (!files.length) return false;
    onPhotoDrop(files);
    return true;
  }, [atMax, onPhotoDrop]);

  useEffect(() => {
    const root = formRef.current;
    if (!root) return;
    const onPaste = (e: ClipboardEvent) => {
      if (ingestClipboardImages(e.clipboardData)) {
        e.preventDefault();
        setIsPasteReady(false);
      }
    };
    root.addEventListener('paste', onPaste);
    return () => root.removeEventListener('paste', onPaste);
  }, [ingestClipboardImages]);
  const openPicker = () => {
    console.log('[photo] openPicker called — atMax:', atMax, 'ref:', photoInputRef.current);
    if (!atMax) {
      photoInputRef.current?.click();
      console.log('[photo] click() triggered on input');
    }
  };
  const handlePhotoDragOver = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    console.log('[photo] dragover');
    if (!atMax) setIsDragOver(true);
  };
  const handlePhotoDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    console.log('[photo] dragleave');
    setIsDragOver(false);
  };
  const handlePhotoDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(false);
    // Try .files first; fall back to .items for browsers that populate only items
    let all: File[] = Array.from(e.dataTransfer.files);
    if (all.length === 0 && e.dataTransfer.items.length > 0) {
      all = Array.from(e.dataTransfer.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      console.log('[photo] drop — used items API, count:', all.length);
    }
    console.log('[photo] drop — all files:', all.map((f) => `${f.name} (${f.type})`));
    if (atMax) { console.log('[photo] drop ignored — at max'); return; }
    const files = all.filter(isContributionImageFile);
    console.log('[photo] image files to process:', files.length);
    if (files.length) onPhotoDrop(files);
  };

  const handlePhotoPaste = (e: React.ClipboardEvent) => {
    if (ingestClipboardImages(e.clipboardData)) {
      e.preventDefault();
      setIsPasteReady(false);
    }
  };

  const nameLabelMap: Record<string, string> = {
    tree: 'Species / Name (optional)',
    road: 'Road Name',
    building: 'Building Name / Number (optional)',
    water: 'Water Body Name (optional)',
    waterway: 'Waterway Name (optional)',
    landuse: 'Name (optional)',
    landcover: 'Name (optional)',
    structure: 'Name (optional)',
    traffic_signal: 'Label (optional)',
    aeroway: 'Name (optional)',
    motorway_junction: 'Junction name (optional)',
    place: 'Place name *',
  };
  const namePlaceholderMap: Record<string, string> = {
    tree: 'e.g., Mango tree, Baobab, Neem',
    road: 'e.g., Main Street, Accra-Tema Motorway',
    building: 'e.g., B12, Accra Mall, Office Block A',
    place: 'e.g., Adjen Kotoku, Osu, Madina',
    landuse: 'e.g., Farmland, School grounds',
    landcover: 'e.g., Grassland patch, Forest area',
    structure: 'e.g., Perimeter wall, Main gate',
    traffic_signal: 'e.g., SL-01, Junction A signal',
    aeroway: 'e.g., Kotoka International Airport, Runway 21',
    motorway_junction: 'e.g., Tema Motorway Exit 12',
    water: 'e.g., Lake Bosomtwe, Kpong Dam',
    waterway: 'e.g., Volta River, Korle Lagoon drain',
  };
  const descPlaceholderMap: Record<string, string> = {
    place: 'e.g., Summary from Wikipedia or a local history source',
    region: 'e.g., Greater Accra is the smallest region by area but the most populous…',
    district: 'e.g., Ga West Municipal covers Adjen Kotoku and surrounding communities…',
    country: 'e.g., Ghana is a country in West Africa…',
    building: 'e.g., Two-storey residential building with a pharmacy on the ground floor',
    poi: 'e.g., Popular local restaurant serving jollof rice, open evenings',
    road: 'e.g., Unpaved road connecting two villages, floods in rainy season',
    tree: 'e.g., Large mango tree at the junction, used as a local landmark',
    aeroway: 'e.g., Asphalt runway serving regional flights, visible from the main road',
    motorway_junction: 'e.g., Exit to Spintex Road from the Accra-Tema motorway',
    water: 'e.g., Small seasonal pond, dries up in harmattan',
  };

  const ftId = featureType?.id ?? '';
  const optionalLabelPoiCategories = new Set(['bench', 'waste_basket']);
  const optionalLabelPathRoadCategories = new Set([
    'footway',
    'path',
    'cycleway',
    'shoulder',
    'parking_lane',
    'pedestrian',
    'steps',
    'bridleway',
  ]);
  const nameFieldLabel = (() => {
    if (ftId === 'poi' && selectedCategory && optionalLabelPoiCategories.has(selectedCategory)) {
      return 'Label (optional)';
    }
    if (ftId === 'road' && selectedCategory && optionalLabelPathRoadCategories.has(selectedCategory)) {
      return 'Road Name (optional)';
    }
    return nameLabelMap[ftId] ?? 'Name *';
  })();
  const nameFieldPlaceholder = (() => {
    if (ftId === 'poi' && selectedCategory === 'bench') return 'e.g., Park bench 3';
    if (ftId === 'poi' && selectedCategory === 'waste_basket') return 'e.g., Bin A, Litter point 2';
    if (ftId === 'road' && selectedCategory && optionalLabelPathRoadCategories.has(selectedCategory)) {
      return 'e.g., Park Path — or leave blank for Footway/Path on map';
    }
    if (ftId === 'landuse' && selectedCategory === 'car_park') return 'e.g., Visitor Car Park, P1';
    return namePlaceholderMap[ftId] ?? "e.g., Kofi's Restaurant";
  })();

  const multiCarriageway = isMultiCarriageway(roadCarriagewayCount);

  const roadFlowValidationError = useMemo(() => {
    if (ftId !== 'road' || roadCarriagewayCount == null) return null;
    const flowErr = validateRoadFlowFields({
      carriagewayCount: roadCarriagewayCount,
      lanes: roadLanes,
      lanesForward: roadLanesForward,
      lanesBackward: roadLanesBackward,
      laneDirections: roadLaneDirections,
      manualLaneDirections: roadManualLaneDirections,
      roadWidth,
      singleStripCaptureMode: multiCarriageway ? undefined : singleStripCaptureMode,
      singleStripTraceReady: multiCarriageway ? undefined : singleStripTraceReady,
      stripACaptureMode: multiCarriageway ? stripACaptureMode : undefined,
      stripBCaptureMode: multiCarriageway ? stripBCaptureMode : undefined,
      stripATraceReady: multiCarriageway ? stripATraceReady : undefined,
      stripBTraceReady: multiCarriageway ? stripBTraceReady : undefined,
      dividedDivideReady: multiCarriageway ? dividedDivideReady : undefined,
      dividedCarriagewayBReady: multiCarriageway ? dividedCarriagewayReady : undefined,
      divideWidthM,
      carriagewayAWidth: roadCarriagewayAWidth,
      carriagewayBWidth: roadCarriagewayBWidth,
      carriagewayALaneDirections: roadCarriagewayALaneDirections,
      carriagewayBLaneDirections: roadCarriagewayBLaneDirections,
      usePerLaneWidths: roadUsePerLaneWidths,
      laneWidthsM: roadLaneWidthsM,
      carriagewayALaneWidthsM: roadCarriagewayALaneWidthsM,
      carriagewayBLaneWidthsM: roadCarriagewayBLaneWidthsM,
    });
    if (flowErr) return flowErr;
    if (!selectedCategory || !supportsSideFacilityTags(selectedCategory)) return null;
    return validateSideFacilityFields({
      category: selectedCategory,
      divided: multiCarriageway,
      state: roadSideFacilities,
      roadWidth,
      carriagewayAWidth: roadCarriagewayAWidth,
      carriagewayBWidth: roadCarriagewayBWidth,
    });
  }, [
    ftId,
    roadCarriagewayCount,
    multiCarriageway,
    roadLanes,
    roadLanesForward,
    roadLanesBackward,
    roadLaneDirections,
    roadManualLaneDirections,
    singleStripCaptureMode,
    singleStripTraceReady,
    stripACaptureMode,
    stripBCaptureMode,
    divideWidthM,
    dividedDivideReady,
    dividedCarriagewayAReady,
    dividedCarriagewayBReady,
    dividedCarriagewayReady,
    stripATraceReady,
    stripBTraceReady,
    roadCarriagewayAWidth,
    roadCarriagewayBWidth,
    roadCarriagewayALaneDirections,
    roadCarriagewayBLaneDirections,
    roadWidth,
    selectedCategory,
    roadSideFacilities,
    roadUsePerLaneWidths,
    roadLaneWidthsM,
    roadCarriagewayALaneWidthsM,
    roadCarriagewayBLaneWidthsM,
  ]);

  const singleStripLaneCount = useMemo(() => {
    if (roadCarriagewayCount !== 1 || multiCarriageway) return null;
    return expectedManualLaneCount({
      carriagewayCount: roadCarriagewayCount,
      lanes: roadLanes,
      lanesForward: roadLanesForward,
      lanesBackward: roadLanesBackward,
    }) ?? 1;
  }, [roadCarriagewayCount, multiCarriageway, roadLanes, roadLanesForward, roadLanesBackward]);

  const syncManualLaneDirections = useCallback((
    lanes: string,
    lanesForward: string,
    lanesBackward: string,
  ) => {
    if (roadCarriagewayCount !== 1 || multiCarriageway) return;
    const expected = expectedManualLaneCount({
      carriagewayCount: roadCarriagewayCount,
      lanes,
      lanesForward,
      lanesBackward,
    });
    if (expected == null) return;
    const total = parseInt(lanes, 10);
    const forward = lanesForward.trim() ? parseInt(lanesForward, 10) : null;
    const backward = lanesBackward.trim() ? parseInt(lanesBackward, 10) : null;
    const seeded = buildLaneDirectionsFromCounts(
      Number.isNaN(total) ? null : total,
      forward != null && !Number.isNaN(forward) ? forward : null,
      backward != null && !Number.isNaN(backward) ? backward : null,
    );
    onRoadLaneDirectionsChange((prev) =>
      resizeLaneDirections(seeded.length > 0 ? seeded : prev, expected),
    );
  }, [
    roadCarriagewayCount,
    multiCarriageway,
    onRoadLaneDirectionsChange,
  ]);

  useEffect(() => {
    if (roadCarriagewayCount !== 1 || multiCarriageway || roadManualLaneDirections) return;
    syncManualLaneDirections(roadLanes, roadLanesForward, roadLanesBackward);
  }, [
    roadCarriagewayCount,
    multiCarriageway,
    roadManualLaneDirections,
    roadLanes,
    roadLanesForward,
    roadLanesBackward,
    syncManualLaneDirections,
  ]);

  useEffect(() => {
    if (!roadUsePerLaneWidths || multiCarriageway) return;
    const expected = expectedManualLaneCount({
      carriagewayCount: roadCarriagewayCount,
      lanes: roadLanes,
      lanesForward: roadLanesForward,
      lanesBackward: roadLanesBackward,
    });
    if (expected == null || !onRoadLaneWidthsMChange) return;
    if (roadLaneWidthsM.length === expected) return;
    onRoadLaneWidthsMChange(resizeLaneWidthStrings(roadLaneWidthsM, expected));
  }, [
    roadUsePerLaneWidths,
    multiCarriageway,
    roadCarriagewayCount,
    roadLanes,
    roadLanesForward,
    roadLanesBackward,
    roadLaneWidthsM.length,
    onRoadLaneWidthsMChange,
  ]);

  useEffect(() => {
    if (!roadUsePerLaneWidths || !multiCarriageway) return;
    const aCount = parseInt(roadLanesForward, 10);
    const bCount = parseInt(roadLanesBackward, 10);
    if (
      !Number.isNaN(aCount)
      && aCount >= 1
      && onRoadCarriagewayALaneWidthsMChange
      && roadCarriagewayALaneWidthsM.length !== aCount
    ) {
      onRoadCarriagewayALaneWidthsMChange(resizeLaneWidthStrings(roadCarriagewayALaneWidthsM, aCount));
    }
    if (
      !Number.isNaN(bCount)
      && bCount >= 1
      && onRoadCarriagewayBLaneWidthsMChange
      && roadCarriagewayBLaneWidthsM.length !== bCount
    ) {
      onRoadCarriagewayBLaneWidthsMChange(resizeLaneWidthStrings(roadCarriagewayBLaneWidthsM, bCount));
    }
  }, [
    roadUsePerLaneWidths,
    multiCarriageway,
    roadLanesForward,
    roadLanesBackward,
    roadCarriagewayALaneWidthsM.length,
    roadCarriagewayBLaneWidthsM.length,
    onRoadCarriagewayALaneWidthsMChange,
    onRoadCarriagewayBLaneWidthsMChange,
  ]);

  const syncDividedCarriagewayLaneDirections = useCallback((
    lanesForward: string,
    lanesBackward: string,
  ) => {
    if (!multiCarriageway) return;
    const aCount = parseInt(lanesForward, 10);
    const bCount = parseInt(lanesBackward, 10);
    if (!Number.isNaN(aCount) && aCount >= 1) {
      onRoadCarriagewayALaneDirectionsChange((prev) => resizeLaneDirections(prev, aCount));
    }
    if (!Number.isNaN(bCount) && bCount >= 1) {
      onRoadCarriagewayBLaneDirectionsChange((prev) => resizeLaneDirections(prev, bCount));
    }
  }, [
    multiCarriageway,
    onRoadCarriagewayALaneDirectionsChange,
    onRoadCarriagewayBLaneDirectionsChange,
  ]);

  useEffect(() => {
    if (!multiCarriageway) return;
    syncDividedCarriagewayLaneDirections(roadLanesForward, roadLanesBackward);
  }, [
    multiCarriageway,
    roadLanesForward,
    roadLanesBackward,
    syncDividedCarriagewayLaneDirections,
  ]);

  const renderCarriagewayLaneDirections = (
    label: string,
    directions: LaneDirection[],
    onChange: (next: LaneDirection[] | ((prev: LaneDirection[]) => LaneDirection[])) => void,
    laneCount: number | null,
  ) => {
    if (!laneCount || laneCount < 1) return null;
    const displayDirections = resizeLaneDirections(directions, laneCount);
    return (
      <div className="space-y-1">
        {label ? (
          <p className="text-xs font-medium text-gray-700 dark:text-neutral-300">{label}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {displayDirections.map((dir, idx) => (
            <div key={`${label}-${idx}`} className="flex items-center gap-1 rounded-lg border border-gray-200 dark:border-neutral-600 px-2 py-1 text-xs">
              <span className="text-gray-500 dark:text-neutral-400">L{idx + 1}</span>
              <select
                value={dir}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const value = e.target.value as LaneDirection;
                  onRoadManualLaneDirectionsChange(true);
                  onChange((prev) => {
                    const base = prev.length > 0 ? prev : displayDirections;
                    const next = [...resizeLaneDirections(base, laneCount)];
                    next[idx] = value;
                    return next;
                  });
                }}
                className="bg-transparent text-gray-800 dark:text-neutral-100 focus:outline-none"
              >
                {LANE_DIRECTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPerLaneWidthInputs = (
    label: string,
    laneCount: number | null,
    widths: string[],
    onWidthsChange: ((v: string[]) => void) | undefined,
  ) => {
    if (!roadUsePerLaneWidths || !onWidthsChange || !laneCount || laneCount < 1) return null;
    const display = resizeLaneWidthStrings(widths, laneCount);
    return (
      <div className="space-y-1 mt-2">
        {label ? (
          <FieldHint label={label} text={ROAD_FLOW_HINTS.perLaneWidths} />
        ) : (
          <FieldHint label="Per-lane widths (m)" text={ROAD_FLOW_HINTS.perLaneWidths} />
        )}
        <div className="flex flex-wrap gap-2">
          {display.map((w, idx) => (
            <label key={`${label}-lw-${idx}`} className="flex flex-col gap-0.5 text-xs">
              <span className="text-gray-500 dark:text-neutral-400">L{idx + 1}</span>
              <input
                type="number"
                min={0.5}
                max={20}
                step={0.1}
                value={w}
                onChange={(e) => {
                  const next = [...display];
                  next[idx] = e.target.value;
                  onWidthsChange(next);
                }}
                className="w-16 rounded-lg border border-gray-200 dark:border-neutral-600 px-2 py-1 text-sm"
              />
            </label>
          ))}
        </div>
      </div>
    );
  };

  const perLaneWidthToggle = onRoadUsePerLaneWidthsChange ? (
    <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-neutral-300 cursor-pointer">
      <input
        type="checkbox"
        checked={roadUsePerLaneWidths}
        onChange={(e) => onRoadUsePerLaneWidthsChange(e.target.checked)}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      Specify per-lane widths (advanced)
    </label>
  ) : null;

  const dividedCarriageway = isMultiCarriageway(roadCarriagewayCount);
  const guidedRoad = ftId === 'road' && roadGuidedSurveyFlow;

  const crossSectionAccordionBadge: AccordionBadge = useMemo(() => {
    if (roadCarriagewayCount == null) return 'Required';
    if (dividedCarriageway) {
      const ok =
        roadLanesForward.trim()
        && roadLanesBackward.trim()
        && roadCarriagewayAWidth.trim()
        && roadCarriagewayBWidth.trim();
      return ok ? 'Complete' : 'Required';
    }
    return roadLanes.trim() && roadWidth.trim() ? 'Complete' : 'Required';
  }, [
    roadCarriagewayCount,
    dividedCarriageway,
    roadLanes,
    roadWidth,
    roadLanesForward,
    roadLanesBackward,
    roadCarriagewayAWidth,
    roadCarriagewayBWidth,
  ]);

  const trafficAccordionBadge: AccordionBadge = useMemo(() => {
    if (roadFlowValidationError) return 'Required';
    if (dividedCarriageway) {
      const nA = parseInt(roadLanesForward, 10) || 0;
      const nB = parseInt(roadLanesBackward, 10) || 0;
      if (
        roadCarriagewayALaneDirections.length === nA
        && roadCarriagewayBLaneDirections.length === nB
        && nA > 0
        && nB > 0
      ) {
        return 'Complete';
      }
      return 'Required';
    }
    const n = parseInt(roadLanes, 10) || 0;
    if (n > 0 && roadLaneDirections.length === n) return 'Complete';
    return 'Optional';
  }, [
    roadFlowValidationError,
    dividedCarriageway,
    roadLanes,
    roadLanesForward,
    roadLanesBackward,
    roadLaneDirections,
    roadCarriagewayALaneDirections,
    roadCarriagewayBLaneDirections,
  ]);

  const updateSegmentMap = (
    mapKey: 'sideFacilitySegments' | 'carriagewayASegments' | 'carriagewayBSegments',
    geometryKey: GeometryFacilityKey,
    side: 'left' | 'right',
    partial: boolean,
    start: string,
    end: string,
  ) => {
    const current = { ...roadSideFacilities[mapKey] } as SideFacilitySegmentMap;
    if (!partial) {
      delete current[geometryKey];
      onRoadSideFacilitiesChange({ [mapKey]: current });
      return;
    }
    current[geometryKey] = [{ side, start, end }];
    onRoadSideFacilitiesChange({ [mapKey]: current });
  };

  const renderFacilityDimensions = (strip: 'A' | 'B' | null) => (
    <div className="space-y-3">
      {FACILITY_DIMENSION_FIELDS.filter((field) =>
        hasActiveFacilityPlacement(dividedCarriageway, roadSideFacilities, field.placementKey),
      ).map((field) => {
        const { widthKey, kerbKey } = facilityDimensionsForScope(
          roadSideFacilities,
          dividedCarriageway,
          strip,
          field,
        );
        const mapKey = strip === 'A'
          ? 'carriagewayASegments'
          : strip === 'B'
            ? 'carriagewayBSegments'
            : 'sideFacilitySegments';
        const segments = segmentMapForScope(roadSideFacilities, dividedCarriageway, strip);
        const segment = segments[field.geometryKey]?.[0];
        const placement = placementForScope(roadSideFacilities, dividedCarriageway, strip, field.placementKey);
        const defaultSide = placement === 'right' ? 'right' : 'left';
        return (
          <div key={`${strip ?? 'single'}-${field.geometryKey}`} className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 p-2 space-y-2">
            <p className="text-xs font-medium text-gray-800 dark:text-neutral-200">{field.label}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div>
                <label className={labelClass}>Width (m) *</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={String(roadSideFacilities[widthKey] ?? '')}
                  onChange={(e) => onRoadSideFacilitiesChange({ [widthKey]: e.target.value })}
                  placeholder={field.defaultWidth}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Kerb gap (m)</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={String(roadSideFacilities[kerbKey] ?? '')}
                  onChange={(e) => onRoadSideFacilitiesChange({ [kerbKey]: e.target.value })}
                  placeholder="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  <FieldHint label="Condition" text={ROAD_FLOW_HINTS.sideFacilityCondition} />
                </label>
                <select
                  value={roadSideFacilities[field.conditionKey] as SideFacilityCondition}
                  onChange={(e) =>
                    onRoadSideFacilitiesChange({
                      [field.conditionKey]: e.target.value as SideFacilityCondition,
                    })
                  }
                  className={inputClass}
                >
                  {SIDE_FACILITY_CONDITION_OPTIONS.map((o) => (
                    <option key={`${field.geometryKey}-cond-${o.value || 'unset'}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  <FieldHint label="Speed limit (km/h)" text={ROAD_FLOW_HINTS.sideFacilityMaxspeed} />
                </label>
                <input
                  type="number"
                  min={5}
                  max={130}
                  step={5}
                  value={String(roadSideFacilities[field.maxspeedKey] ?? '')}
                  onChange={(e) => onRoadSideFacilitiesChange({ [field.maxspeedKey]: e.target.value })}
                  placeholder="inherit"
                  className={inputClass}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(segment)}
                onChange={(e) =>
                  updateSegmentMap(
                    mapKey,
                    field.geometryKey,
                    segment?.side ?? defaultSide,
                    e.target.checked,
                    segment?.start ?? '0',
                    segment?.end ?? '1',
                  )
                }
                className="rounded border-gray-300 dark:border-neutral-600"
              />
              Partial length only (not full trace)
            </label>
            {segment ? (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={labelClass}>Side</label>
                  <select
                    value={segment.side}
                    onChange={(e) =>
                      updateSegmentMap(
                        mapKey,
                        field.geometryKey,
                        e.target.value as 'left' | 'right',
                        true,
                        segment.start,
                        segment.end,
                      )
                    }
                    className={inputClass}
                  >
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Start (0–1)</label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={segment.start}
                    onChange={(e) =>
                      updateSegmentMap(
                        mapKey,
                        field.geometryKey,
                        segment.side,
                        true,
                        e.target.value,
                        segment.end,
                      )
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>End (0–1)</label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={segment.end}
                    onChange={(e) =>
                      updateSegmentMap(
                        mapKey,
                        field.geometryKey,
                        segment.side,
                        true,
                        segment.start,
                        e.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </div>
              </div>
            ) : null}
            {segment ? (
              <p className="text-[11px] text-gray-500 dark:text-neutral-500">
                Ratios run along your traced centreline — 0 is the start you drew, 1 is the end. No extra points needed.
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const carriagewayAccessFields = [
    { key: 'foot' as const, label: 'Foot' },
    { key: 'bicycle' as const, label: 'Bicycle' },
    { key: 'bus' as const, label: 'Bus / trotro' },
    { key: 'psv' as const, label: 'Public service vehicles (PSV)' },
    { key: 'access' as const, label: 'General access' },
  ] as const;

  const kerbsideStackHint = (
    <p className="text-[11px] text-gray-500 dark:text-neutral-500 font-mono leading-relaxed">
      Carriageway → parking → shoulder → cycle → sidewalk → outer edge (fixed stack per side)
    </p>
  );

  const dividedCrossSectionHint = (
    <div className="rounded-lg border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 px-3 py-2 text-[11px] text-gray-600 dark:text-neutral-400 font-mono leading-snug">
      <p className="text-center text-gray-500 dark:text-neutral-500 mb-1">OUTER (property) ··· DIVIDE SIDE (inner)</p>
      <p>{'┌───────────────┬──────────────────────┐'}</p>
      <p>{'│  Outer kerb   │  Inner kerb          │'}</p>
      <p>{'└───────────────┴──────────────────────┘'}</p>
    </div>
  );

  const renderKerbsideFacilitiesContent = () => (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Kerbside facilities (Model A)</p>
        <p className="text-xs text-gray-500 dark:text-neutral-500">
          {dividedCarriageway
            ? 'Configure facilities adjoining each strip. Choose inner (divide) or outer (property) side.'
            : ROAD_FLOW_HINTS.kerbsideModelAIntro}
        </p>
      </div>
      {kerbsideStackHint}
      {dividedCarriageway ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(['A', 'B'] as const).map((side) => (
            <div key={side} className="space-y-2">
              <p
                className={
                  side === 'A'
                    ? 'text-xs font-medium text-sky-700 dark:text-sky-300'
                    : 'text-xs font-medium text-orange-700 dark:text-orange-300'
                }
              >
                Carriageway {side}
              </p>
              {dividedCrossSectionHint}
              {CARRIAGEWAY_SIDE_PLACEMENT_FIELDS.map(({ dividedKey, label }) => {
                const fieldKey = carriagewaySideFieldKey(side, dividedKey);
                return (
                  <div key={`${side}-${dividedKey}`}>
                    <label className={labelClass}>{label}</label>
                    <select
                      value={roadSideFacilities[fieldKey] as StripKerbPlacement}
                      onChange={(e) =>
                        onRoadSideFacilitiesChange({
                          [fieldKey]: e.target.value as StripKerbPlacement,
                        })
                      }
                      className={inputClass}
                    >
                      {DIVIDED_KERB_PLACEMENT_OPTIONS.map((o) => (
                        <option key={`${side}-${dividedKey}-${o.value || 'unset'}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
              {renderFacilityDimensions(side)}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {CARRIAGEWAY_SIDE_PLACEMENT_FIELDS.map(({ stateKey, label }) => (
            <div key={stateKey}>
              <label className={labelClass}>{label}</label>
              <select
                value={roadSideFacilities[stateKey]}
                onChange={(e) =>
                  onRoadSideFacilitiesChange({ [stateKey]: e.target.value as SidePlacement })
                }
                className={inputClass}
              >
                {SIDE_PLACEMENT_OPTIONS.map((o) => (
                  <option key={`${stateKey}-${o.value || 'unset'}`} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
      <div>
        <label className={labelClass}>Kerb gutter</label>
        <select
          value={roadSideFacilities.gutter}
          onChange={(e) => onRoadSideFacilitiesChange({ gutter: e.target.value as GutterTag })}
          className={inputClass}
        >
          {GUTTER_OPTIONS.map((o) => (
            <option key={`gutter-${o.value || 'unset'}`} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {!dividedCarriageway ? renderFacilityDimensions(null) : null}
      <p className="text-xs text-gray-500 dark:text-neutral-500">{ROAD_FLOW_HINTS.kerbsideSectionFooter}</p>
    </div>
  );

  const renderCarriagewayAccessContent = () => (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-neutral-500">{ROAD_FLOW_HINTS.carriagewayAccessIntro}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {carriagewayAccessFields.map(({ key, label }) => (
          <div key={key}>
            <label className={labelClass}>{label}</label>
            <select
              value={roadSideFacilities[key]}
              onChange={(e) => onRoadSideFacilitiesChange({ [key]: e.target.value as AccessTag })}
              className={inputClass}
            >
              {ACCESS_TAG_OPTIONS.map((o) => (
                <option key={`${key}-${o.value || 'unset'}`} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );

  const showContactAndOnline = isFullDetails && contributionFeatureShowsBusinessDetails(ftId, landmarkSourceGroup);
  const showPoiExtras = isFullDetails && showContactAndOnline && (ftId === 'poi' || ftId === 'landmark');
  const gpsBad = !!ghanaPostGps && !GPS_REGEX.test(ghanaPostGps);
  const officialGpsBad =
    metadataOnly
    && isGssBoundaryPlaceCategory(selectedCategory)
    && !!ghanaPostGps.trim()
    && !/^[A-Z0-9-]{2,14}$/.test(ghanaPostGps.trim());
  const showDescription =
    !metadataOnly || metadataUpdateIntent === 'description' || metadataUpdateIntent === 'both';
  const showPhotos =
    !metadataOnly || metadataUpdateIntent === 'photos' || metadataUpdateIntent === 'both';

  return (
    <div ref={formRef} className="space-y-4">
      {/* Category badge — hidden for metadata-only official area */}
      {categoryMeta && !metadataOnly && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-primary-200 bg-primary-50/80 px-3 py-2.5 dark:border-neutral-700 dark:bg-neutral-800/90">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ring-1 ring-black/5 dark:ring-white/10 ${(colorClasses[categoryMeta.color] || colorClasses.gray).bg}`}>
              <DynamicIcon name={categoryMeta.icon} size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-primary-700 dark:text-neutral-400 font-medium">Category</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100 truncate">{categoryMeta.name}</p>
            </div>
          </div>
          <button type="button" onClick={onChangeCategoryStep} className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0">
            Change
          </button>
        </div>
      )}

      {metadataOnly && officialBoundaryLabel && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/80 px-3 py-2.5 dark:border-neutral-700 dark:bg-neutral-800/90">
          <p className="text-xs text-indigo-700 dark:text-neutral-400 font-medium">Official area</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">{officialBoundaryLabel}</p>
        </div>
      )}

      {metadataOnly && isGssBoundaryPlaceCategory(selectedCategory) && (
        <div>
          <label className={labelClass}>{ghanaPostGpsAdminCodeLabel(selectedCategory)} (optional)</label>
          <input
            type="text"
            value={ghanaPostGps}
            onChange={(e) => {
              let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
              if (v.length > 14) v = v.slice(0, 14);
              onGhanaPostGpsChange(v);
            }}
            maxLength={14}
            placeholder="e.g., AK, A2, or GH-GA141-2554"
            className={`${inputClass} ${officialGpsBad ? 'border-amber-400 dark:border-amber-600' : ''}`}
          />
          {officialGpsBad && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Use 2–14 letters, numbers, or hyphens (Ghana PostGPS district/region code).
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
            Official Ghana PostGPS code, if you know it. Coordal codes are assigned automatically.
          </p>
        </div>
      )}

      {/* Name */}
      {!metadataOnly && (
      <div>
        <label className={labelClass}>{nameFieldLabel}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={nameFieldPlaceholder}
          className={inputClass}
        />
        {ftId === 'building' && (
          <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1 leading-relaxed">
            Edit this if you know the building name (e.g. Accra Mall). Leave as the number or blank if unnamed.
          </p>
        )}
      </div>
      )}

      {/* Address section */}
      {!metadataOnly && (
      <div className="rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-gray-800 dark:text-neutral-200">
            {ftId === 'place' ? 'Located within' : isFullDetails ? 'Address' : 'Location'}
          </span>
          {addressLookupStatus === 'loading' && (
            <span className="text-xs text-gray-500 dark:text-neutral-400 flex items-center gap-1.5">
              <Loader2 size={14} className="animate-spin" /> From map location…
            </span>
          )}
          {addressLookupStatus === 'ok' && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400">Prefilled, edit as needed</span>
          )}
          {addressLookupStatus === 'error' && (
            <span className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
              Enter manually
              {onRetryAddressLookup && (
                <button
                  type="button"
                  onClick={onRetryAddressLookup}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Retry lookup
                </button>
              )}
            </span>
          )}
        </div>

        <p className="text-xs text-gray-500 dark:text-neutral-400">
          {ftId === 'place'
            ? 'Parent district and region only. The place name goes in the field above, not here.'
            : isFullDetails
              ? 'We use your pin, drawn area, or trace to look up street and admin areas. Fill in anything missing before you submit.'
              : 'Auto-filled from your map location. Correct if needed.'}
        </p>
        {suggestedAddressFields.length > 0 && addressLookupStatus === 'ok' && (
          <p className="text-xs text-amber-800 dark:text-neutral-300 rounded-lg bg-amber-50/60 dark:bg-neutral-700/40 px-2.5 py-2 leading-relaxed">
            {suggestedAddressFields.includes('suburb') && suggestedAddressFields.includes('city')
              ? 'City/town and area/suburb are suggested from nearby boundaries. Please confirm or change them.'
              : suggestedAddressFields.includes('suburb')
                ? 'Area/suburb is suggested from a nearby boundary. Please confirm or change it.'
                : 'City/town is suggested from a nearby boundary. Please confirm or change it.'}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <SearchableBoundaryInput
              label="Region"
              value={addrRegion}
              onChange={onAddrRegionChange}
              fetchOptions={fetchRegions}
              minQueryLength={0}
              strict
              placeholder="Search or select region"
              disabled={addressLookupStatus === 'loading'}
              inputClass={inputClass}
              labelClass={labelClass}
            />
          </div>
          <div className="sm:col-span-2">
            <SearchableBoundaryInput
              label="District"
              value={addrDistrict}
              onChange={onAddrDistrictChange}
              fetchOptions={fetchDistricts}
              minQueryLength={0}
              strict
              placeholder={addrRegion ? `Search districts in ${addrRegion}` : 'Pick region first, or search all districts'}
              disabled={addressLookupStatus === 'loading'}
              inputClass={inputClass}
              labelClass={labelClass}
            />
          </div>
          {ftId !== 'place' && (
          <div className="sm:col-span-2">
            <SearchableBoundaryInput
              label="City / Town"
              value={addrCity}
              onChange={onAddrCityChange}
              fetchOptions={fetchTowns}
              minQueryLength={addrDistrict ? 0 : 2}
              placeholder={addrDistrict ? `Search cities/towns in ${addrDistrict}` : 'Type at least 2 letters to search'}
              hint="Pick from suggestions when available, or type your own."
              disabled={addressLookupStatus === 'loading'}
              inputClass={inputClass}
              labelClass={labelClass}
            />
          </div>
          )}

          {showLocationSuburb && (
            <div className="sm:col-span-2">
              <SearchableBoundaryInput
                label="Area / Suburb"
                value={addrSuburb}
                onChange={onAddrSuburbChange}
                fetchOptions={fetchSuburbs}
                minQueryLength={addrDistrict ? 0 : 2}
                placeholder={addrDistrict ? `Search areas in ${addrDistrict}` : 'Type at least 2 letters to search'}
                hint="Pick from suggestions when available, or type your own."
                disabled={addressLookupStatus === 'loading'}
                inputClass={inputClass}
                labelClass={labelClass}
              />
            </div>
          )}

          {ftId === 'place' && !isGssBoundaryPlaceCategory(selectedCategory) && (
            <div className="sm:col-span-2">
              <label className={labelClass}>{ghanaPostGpsAdminCodeLabel(selectedCategory)} (optional)</label>
              <input
                type="text"
                value={ghanaPostGps}
                onChange={(e) => {
                  let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                  if (v.length > 14) v = v.slice(0, 14);
                  onGhanaPostGpsChange(v);
                }}
                maxLength={24}
                placeholder="e.g., AK, A2, or GH-GA141-2554"
                className={`${inputClass} ${officialGpsBad ? 'border-amber-400 dark:border-amber-600' : ''}`}
              />
              {officialGpsBad && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Use 2–14 letters, numbers, or hyphens.
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
                Ghana PostGPS code for this area, if you know it. Coordal codes are assigned automatically.
              </p>
            </div>
          )}

          {isFullDetails && ftId !== 'place' && (
            <>
              <div className="sm:col-span-2">
                <label className={labelClass}>Street</label>
                {lockHouseNumber && houseNumber.trim() ? (
                  <div className="flex rounded-lg border border-gray-300 dark:border-neutral-600 overflow-hidden bg-white dark:bg-neutral-700 focus-within:ring-2 focus-within:ring-primary-400">
                    <span
                      className="inline-flex items-center px-3 py-2 text-sm font-semibold font-mono text-gray-700 dark:text-neutral-200 bg-gray-100 dark:bg-neutral-800 border-r border-gray-300 dark:border-neutral-600 shrink-0"
                      aria-label={`House number ${houseNumber}`}
                    >
                      {houseNumber}
                    </span>
                    <input
                      type="text"
                      value={addrStreet}
                      onChange={(e) => onAddrStreetChange(e.target.value)}
                      placeholder="Street name"
                      className="flex-1 min-w-0 px-3 py-2 text-sm bg-transparent text-gray-900 dark:text-neutral-100 focus:outline-none placeholder-gray-400 dark:placeholder-neutral-500"
                    />
                  </div>
                ) : (
                  <input type="text" value={addrStreet} onChange={(e) => onAddrStreetChange(e.target.value)} placeholder="Street name" className={inputClass} />
                )}
                {lockHouseNumber && houseNumber.trim() && (
                  <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
                    House number is fixed from the map building. Edit the street name only.
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Ghana Post GPS</label>
                <input
                  type="text"
                  value={ghanaPostGps}
                  onChange={(e) => onGhanaPostGpsChange(sanitizeGhanaPostGpsInput(e.target.value))}
                  maxLength={GHANA_POST_GPS_MAX_LENGTH}
                  placeholder="e.g., AK-039-5028 or GH-GA141-2554"
                  className={`${inputClass} ${gpsBad ? 'border-amber-400 dark:border-amber-600' : ''}`}
                />
                {gpsBad && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Format: XX-NNN-NNNN or GH-XXNNN-NNNN (e.g., AK-039-5028 or GH-GA141-2554)
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {addressLine.trim() && (
          <p className="text-xs text-gray-600 dark:text-neutral-400 pt-1 border-t border-gray-200 dark:border-neutral-600">
            <span className="font-medium text-gray-700 dark:text-neutral-300">Preview: </span>
            {addressLine}
          </p>
        )}
      </div>
      )}

      {/* Contact + Online Presence + Opening Hours — POI/building only */}
      {showContactAndOnline && (
        <div className="rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 p-3 space-y-3">
          <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200 flex items-center gap-1.5">
            <Phone size={14} className="text-gray-500 dark:text-neutral-400" />
            Contact
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onBlur={() => setPhoneError(validateContactPhone(phone, 'Phone'))}
                placeholder="0241234567"
                maxLength={24}
                className={`${inputClass}${phoneError ? ' border-amber-400 dark:border-amber-600' : ''}`}
              />
              {phoneError && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{phoneError}</p>}
            </div>
            <div>
              <label className={labelClass}>Mobile</label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => handleMobileChange(e.target.value)}
                onBlur={() => setMobileError(validateContactPhone(mobile, 'Mobile'))}
                placeholder="0241234567"
                maxLength={24}
                className={`${inputClass}${mobileError ? ' border-amber-400 dark:border-amber-600' : ''}`}
              />
              {mobileError && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{mobileError}</p>}
            </div>
            <div>
              <label className={labelClass}>WhatsApp</label>
              <input
                type="tel"
                value={whatsapp}
                onChange={(e) => handleWhatsappChange(e.target.value)}
                onBlur={() => setWhatsappError(validateContactPhone(whatsapp, 'WhatsApp'))}
                placeholder="0241234567"
                maxLength={24}
                className={`${inputClass}${whatsappError ? ' border-amber-400 dark:border-amber-600' : ''}`}
              />
              {whatsappError && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{whatsappError}</p>}
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" value={contactEmail} onChange={(e) => onContactEmailChange(e.target.value)} placeholder="info@example.com" className={inputClass} />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-neutral-500">
            Phone numbers: up to 15 digits; spaces and (0) are fine (e.g. +233 (0) 23 3099 096 or 0241234567).
          </p>

          <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200 flex items-center gap-1.5 pt-1">
            <Globe size={14} className="text-gray-500 dark:text-neutral-400" />
            Online Presence
          </p>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Website</label>
              <input type="url" value={website} onChange={(e) => onWebsiteChange(e.target.value)} placeholder="https://example.com" className={inputClass} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Facebook</label>
                <input type="text" value={facebook} onChange={(e) => onFacebookChange(e.target.value)} placeholder="Page name or URL" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Instagram</label>
                <input type="text" value={instagram} onChange={(e) => onInstagramChange(e.target.value)} placeholder="@handle or URL" className={inputClass} />
              </div>
            </div>
          </div>

          <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200 flex items-center gap-1.5 pt-1">
            <Clock size={14} className="text-gray-500 dark:text-neutral-400" />
            Opening Hours
          </p>
          <OpeningHoursEditor value={openingHours} onChange={onOpeningHoursChange} />
        </div>
      )}

      {ftId === 'building' && ['apartments', 'commercial', 'office', 'hotel'].includes(selectedCategory || '') && (
        <p className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 rounded-lg px-3 py-2">
          This building type often has multiple units or rooms — add them in the next step.
        </p>
      )}

      {/* Road-specific */}
      {ftId === 'road' && (
        <div className="space-y-4">
          {guidedRoad && roadLayoutSubtitle ? (
            <RoadGeometryStatusBar
              layoutSubtitle={roadLayoutSubtitle}
              rows={roadGeometryStatusRows}
              onEditGeometry={onEditRoadGeometry}
            />
          ) : null}
          {!guidedRoad ? (
          <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/60 dark:bg-neutral-800/30 p-3">
            <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Road layout</p>
          <div>
            <label className={labelClass}>
              <FieldHint label="Carriageways *" text={ROAD_FLOW_HINTS.carriagewayCount} />
            </label>
            <div className="flex flex-wrap gap-3 text-sm">
              {([
                { count: 1, label: 'Single strip' },
                { count: 2, label: 'Divided (median / dual carriageway)' },
              ] as const).map((opt) => (
                <label key={opt.count} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-neutral-300">
                  <input
                    type="radio"
                    name="road-carriageway-count"
                    checked={roadCarriagewayCount === opt.count}
                    onChange={() => {
                      onRoadCarriagewayCountChange(opt.count);
                      onRoadLaneDirectionsChange([]);
                      onRoadManualLaneDirectionsChange(false);
                    }}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {roadCarriagewayCount == null ? (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Required. How many separate carriageways does this road have?</p>
            ) : null}
          </div>
          {multiCarriageway ? (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/80 dark:bg-amber-950/20 p-3 space-y-3">
              {dividedSurveySetupHint ? (
                <p className="text-xs text-amber-900 dark:text-amber-100 bg-amber-100/80 dark:bg-amber-950/50 rounded-lg px-2.5 py-2">
                  Divided layout needs its own <strong>divide</strong> line (median or pavement seam), not your
                  single-strip trace. Trace the divide below, then set each carriageway capture mode and widths.
                  {onDismissDividedSurveySetupHint ? (
                    <button
                      type="button"
                      className="ml-2 underline font-medium"
                      onClick={onDismissDividedSurveySetupHint}
                    >
                      Got it
                    </button>
                  ) : null}
                </p>
              ) : null}
              <p className="text-xs font-semibold text-amber-950 dark:text-amber-100">Divide</p>
              <p className="text-xs text-gray-700 dark:text-neutral-300">
                {dividedDivideReady ? 'Divide line captured on map ✓' : 'No divide line yet — trace the median or seam'}
              </p>
              <div>
                <label className={labelClass}>
                  <FieldHint label="Barrier width (m)" text={ROAD_FLOW_HINTS.divideWidthM} />
                </label>
                <input
                  type="number"
                  min={0}
                  max={80}
                  step={0.1}
                  value={divideWidthM}
                  onChange={(e) => onDivideWidthMChange(e.target.value)}
                  placeholder="0 = pavement seam"
                  className={inputClass}
                />
              </div>
              {onStartMarkingDivide ? (
                <button
                  type="button"
                  onClick={onStartMarkingDivide}
                  className="w-full py-2 text-sm font-semibold rounded-lg border border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-950/40"
                >
                  {dividedDivideReady ? 'Update divide trace' : 'Trace divide on map'}
                </button>
              ) : null}
              <p className="text-xs font-semibold text-amber-950 dark:text-amber-100 pt-2 border-t border-amber-200/80 dark:border-amber-900/40">
                Carriageways A & B
              </p>
              {(['A', 'B'] as const).map((side) => {
                const mode = side === 'A' ? stripACaptureMode : stripBCaptureMode;
                const onMode = side === 'A' ? onStripACaptureModeChange : onStripBCaptureModeChange;
                const ready = side === 'A' ? dividedCarriagewayAReady : dividedCarriagewayBReady;
                const traceReady = side === 'A' ? stripATraceReady : stripBTraceReady;
                const onMark = side === 'A' ? onStartMarkingStripA : onStartMarkingStripB;
                const modes = captureModesForLayout('divided');
                const geometryStatus = (() => {
                  if (mode === 'derive_from_divide') {
                    return ready ? 'derived ✓' : 'needs divide + width';
                  }
                  if (ready) return 'ready ✓';
                  if (traceReady && mode === 'trace_outer_kerb') {
                    return 'map trace ✓ — enter paved width below';
                  }
                  if (traceReady) return 'map trace ✓';
                  return 'needs map trace';
                })();
                return (
                  <div key={side} className="space-y-2 rounded-md border border-amber-100 dark:border-amber-900/40 p-2">
                    <p className="text-xs font-medium text-gray-800 dark:text-neutral-200">Carriageway {side}</p>
                    <select
                      value={mode}
                      onChange={(e) => onMode(e.target.value as StripCaptureMode)}
                      className={inputClass}
                    >
                      {modes.map((m) => {
                        const opt = STRIP_CAPTURE_MODE_OPTIONS.find((o) => o.value === m);
                        return (
                          <option key={m} value={m}>{opt?.label ?? m}</option>
                        );
                      })}
                    </select>
                    <p className="text-xs text-gray-600 dark:text-neutral-400">
                      {STRIP_CAPTURE_MODE_OPTIONS.find((o) => o.value === mode)?.hint}
                    </p>
                    <p className="text-xs text-gray-700 dark:text-neutral-300">
                      Geometry: {geometryStatus}
                    </p>
                    {mode !== 'derive_from_divide' && onMark ? (
                      <button
                        type="button"
                        onClick={onMark}
                        className="w-full py-1.5 text-xs font-semibold rounded-lg border border-amber-300/80 dark:border-amber-800 text-amber-900 dark:text-amber-100"
                      >
                        {traceReady ? `Update strip ${side} trace` : `Trace strip ${side} on map`}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : roadCarriagewayCount === 1 ? (
            <div className="rounded-lg border border-gray-200 dark:border-neutral-600 p-3 space-y-2">
              <label className={labelClass}>
                <FieldHint label="Strip capture" text={ROAD_FLOW_HINTS.stripCaptureMode} />
              </label>
              <select
                value={singleStripCaptureMode}
                onChange={(e) => onSingleStripCaptureModeChange(e.target.value as StripCaptureMode)}
                className={inputClass}
              >
                {captureModesForLayout('single').map((m) => {
                  const opt = STRIP_CAPTURE_MODE_OPTIONS.find((o) => o.value === m);
                  return <option key={m} value={m}>{opt?.label ?? m}</option>;
                })}
              </select>
              <p className="text-xs text-gray-600 dark:text-neutral-400">
                {STRIP_CAPTURE_MODE_OPTIONS.find((o) => o.value === singleStripCaptureMode)?.hint}
              </p>
              <p className="text-xs text-gray-700 dark:text-neutral-300">
                Map: {singleStripTraceReady ? 'captured ✓' : 'trace required'}
              </p>
              {onStartMarkingSingleStrip ? (
                <button
                  type="button"
                  onClick={onStartMarkingSingleStrip}
                  className="w-full py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-neutral-600"
                >
                  {singleStripTraceReady ? 'Re-trace on map' : 'Trace on map'}
                </button>
              ) : null}
            </div>
          ) : null}
          </div>
          ) : null}

          {roadCarriagewayCount != null ? (
          guidedRoad ? (
            <RoadDetailsAccordionSection
              id="road-cross"
              title="B. Carriageway configuration"
              badge={crossSectionAccordionBadge}
              defaultOpen
            >
            <div className="grid grid-cols-2 gap-3">
            {multiCarriageway ? (
              <div className="col-span-2">
                <label className={labelClass}>
                  <FieldHint label="Barrier width (m)" text={ROAD_FLOW_HINTS.divideWidthM} />
                </label>
                <input
                  type="number"
                  min={0}
                  max={80}
                  step={0.1}
                  value={divideWidthM}
                  onChange={(e) => onDivideWidthMChange(e.target.value)}
                  placeholder="0 = pavement seam"
                  className={inputClass}
                />
              </div>
            ) : null}
            {!multiCarriageway ? (
            <div>
              <label className={labelClass}>
                <FieldHint label="Total lanes *" text={ROAD_FLOW_HINTS.totalLanes} />
              </label>
              <input
                type="number"
                min={1}
                max={12}
                value={roadLanes}
                onChange={(e) => {
                  const next = e.target.value;
                  onRoadLanesChange(next);
                  onRoadManualLaneDirectionsChange(false);
                  syncManualLaneDirections(next, roadLanesForward, roadLanesBackward);
                }}
                placeholder="e.g. 2"
                className={inputClass}
              />
            </div>
            ) : null}
            {multiCarriageway ? (
              <>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway A lanes"
                      text={ROAD_FLOW_HINTS.forwardLanes}
                    />
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={roadLanesForward}
                    onChange={(e) => {
                      const next = e.target.value;
                      onRoadLanesForwardChange(next);
                      syncDividedCarriagewayLaneDirections(next, roadLanesBackward);
                    }}
                    placeholder="e.g. 2"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway B lanes"
                      text={ROAD_FLOW_HINTS.backwardLanes}
                    />
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={roadLanesBackward}
                    onChange={(e) => {
                      const next = e.target.value;
                      onRoadLanesBackwardChange(next);
                      syncDividedCarriagewayLaneDirections(roadLanesForward, next);
                    }}
                    placeholder="e.g. 2"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway A width (m) *"
                      text={ROAD_FLOW_HINTS.carriagewayAWidth}
                    />
                  </label>
                  <input
                    type="number"
                    min={2}
                    max={60}
                    step={0.1}
                    value={roadCarriagewayAWidth}
                    onChange={(e) => onRoadCarriagewayAWidthChange(e.target.value)}
                    placeholder="e.g. 5"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway B width (m) *"
                      text={ROAD_FLOW_HINTS.carriagewayBWidth}
                    />
                  </label>
                  <input
                    type="number"
                    min={2}
                    max={60}
                    step={0.1}
                    value={roadCarriagewayBWidth}
                    onChange={(e) => onRoadCarriagewayBWidthChange(e.target.value)}
                    placeholder="e.g. 6"
                    className={inputClass}
                  />
                </div>
              </>
            ) : (
              <div className="col-span-2">
                <label className={labelClass}>
                  <FieldHint
                    label="Road width (m) *"
                    text={ROAD_FLOW_HINTS.roadWidth}
                  />
                </label>
                <input
                  type="number"
                  min={2}
                  max={60}
                  step={0.1}
                  value={roadWidth}
                  onChange={(e) => onRoadWidthChange(e.target.value)}
                  placeholder="e.g. 7"
                  className={inputClass}
                />
              </div>
            )}
            </div>
            </RoadDetailsAccordionSection>
          ) : (
          <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/60 dark:bg-neutral-800/30 p-3">
            <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Carriageway configuration</p>
            <div className="grid grid-cols-2 gap-3">
            {!multiCarriageway ? (
            <div>
              <label className={labelClass}>
                <FieldHint label="Total lanes *" text={ROAD_FLOW_HINTS.totalLanes} />
              </label>
              <input
                type="number"
                min={1}
                max={12}
                value={roadLanes}
                onChange={(e) => {
                  const next = e.target.value;
                  onRoadLanesChange(next);
                  onRoadManualLaneDirectionsChange(false);
                  syncManualLaneDirections(next, roadLanesForward, roadLanesBackward);
                }}
                placeholder="e.g. 2"
                className={inputClass}
              />
            </div>
            ) : null}
            {multiCarriageway ? (
              <>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway A lanes"
                      text={ROAD_FLOW_HINTS.forwardLanes}
                    />
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={roadLanesForward}
                    onChange={(e) => {
                      const next = e.target.value;
                      onRoadLanesForwardChange(next);
                      syncDividedCarriagewayLaneDirections(next, roadLanesBackward);
                    }}
                    placeholder="e.g. 2"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway B lanes"
                      text={ROAD_FLOW_HINTS.backwardLanes}
                    />
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={roadLanesBackward}
                    onChange={(e) => {
                      const next = e.target.value;
                      onRoadLanesBackwardChange(next);
                      syncDividedCarriagewayLaneDirections(roadLanesForward, next);
                    }}
                    placeholder="e.g. 2"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway A width (m) *"
                      text={ROAD_FLOW_HINTS.carriagewayAWidth}
                    />
                  </label>
                  <input
                    type="number"
                    min={2}
                    max={60}
                    step={0.1}
                    value={roadCarriagewayAWidth}
                    onChange={(e) => onRoadCarriagewayAWidthChange(e.target.value)}
                    placeholder="e.g. 5"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <FieldHint
                      label="Carriageway B width (m) *"
                      text={ROAD_FLOW_HINTS.carriagewayBWidth}
                    />
                  </label>
                  <input
                    type="number"
                    min={2}
                    max={60}
                    step={0.1}
                    value={roadCarriagewayBWidth}
                    onChange={(e) => onRoadCarriagewayBWidthChange(e.target.value)}
                    placeholder="e.g. 6"
                    className={inputClass}
                  />
                </div>
              </>
            ) : (
              <div className="col-span-2">
                <label className={labelClass}>
                  <FieldHint
                    label="Road width (m) *"
                    text={ROAD_FLOW_HINTS.roadWidth}
                  />
                </label>
                <input
                  type="number"
                  min={2}
                  max={60}
                  step={0.1}
                  value={roadWidth}
                  onChange={(e) => onRoadWidthChange(e.target.value)}
                  placeholder="e.g. 7"
                  className={inputClass}
                />
              </div>
            )}
            </div>
          </div>
          )
          ) : null}

          {roadCarriagewayCount != null ? (
          guidedRoad ? (
            <RoadDetailsAccordionSection
              id="road-traffic"
              title="C. Traffic flow"
              badge={trafficAccordionBadge}
            >
            <p className="text-xs text-gray-500 dark:text-neutral-400">{TRAFFIC_FLOW_DIRECTIONS_INTRO}</p>
            {perLaneWidthToggle}
            {multiCarriageway ? (
            <div className="space-y-3">
              <FieldHint
                label="Lane directions (required per carriageway)"
                text={ROAD_FLOW_HINTS.dividedLaneDirections}
              />
              {renderCarriagewayLaneDirections(
                'Carriageway A — L1 left, facing draw direction',
                roadCarriagewayALaneDirections,
                onRoadCarriagewayALaneDirectionsChange,
                parseInt(roadLanesForward, 10) || null,
              )}
              {renderPerLaneWidthInputs(
                'Carriageway A — lane widths',
                parseInt(roadLanesForward, 10) || null,
                roadCarriagewayALaneWidthsM,
                onRoadCarriagewayALaneWidthsMChange,
              )}
              {renderCarriagewayLaneDirections(
                'Carriageway B — L1 left, facing draw direction',
                roadCarriagewayBLaneDirections,
                onRoadCarriagewayBLaneDirectionsChange,
                parseInt(roadLanesBackward, 10) || null,
              )}
              {renderPerLaneWidthInputs(
                'Carriageway B — lane widths',
                parseInt(roadLanesBackward, 10) || null,
                roadCarriagewayBLaneWidthsM,
                onRoadCarriagewayBLaneWidthsMChange,
              )}
            </div>
            ) : null}
            {roadCarriagewayCount === 1 ? (
            <div className="space-y-2">
              <FieldHint
                label={`Lane directions (${singleStripLaneCount ?? 1} lane${(singleStripLaneCount ?? 1) === 1 ? '' : 's'})`}
                text={ROAD_FLOW_HINTS.laneDirections}
              />
              {renderCarriagewayLaneDirections(
                '',
                roadLaneDirections,
                onRoadLaneDirectionsChange,
                singleStripLaneCount,
              )}
              {renderPerLaneWidthInputs(
                '',
                singleStripLaneCount,
                roadLaneWidthsM,
                onRoadLaneWidthsMChange,
              )}
            </div>
            ) : null}
            </RoadDetailsAccordionSection>
          ) : (
          <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/60 dark:bg-neutral-800/30 p-3">
            <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Traffic flow</p>
            <p className="text-xs text-gray-500 dark:text-neutral-400">{TRAFFIC_FLOW_DIRECTIONS_INTRO}</p>
            {perLaneWidthToggle}
            {multiCarriageway ? (
            <div className="space-y-3">
              <FieldHint
                label="Lane directions (required per carriageway)"
                text={ROAD_FLOW_HINTS.dividedLaneDirections}
              />
              {renderCarriagewayLaneDirections(
                'Carriageway A — L1 left, facing draw direction',
                roadCarriagewayALaneDirections,
                onRoadCarriagewayALaneDirectionsChange,
                parseInt(roadLanesForward, 10) || null,
              )}
              {renderPerLaneWidthInputs(
                'Carriageway A — lane widths',
                parseInt(roadLanesForward, 10) || null,
                roadCarriagewayALaneWidthsM,
                onRoadCarriagewayALaneWidthsMChange,
              )}
              {renderCarriagewayLaneDirections(
                'Carriageway B — L1 left, facing draw direction',
                roadCarriagewayBLaneDirections,
                onRoadCarriagewayBLaneDirectionsChange,
                parseInt(roadLanesBackward, 10) || null,
              )}
              {renderPerLaneWidthInputs(
                'Carriageway B — lane widths',
                parseInt(roadLanesBackward, 10) || null,
                roadCarriagewayBLaneWidthsM,
                onRoadCarriagewayBLaneWidthsMChange,
              )}
            </div>
            ) : null}
            {roadCarriagewayCount === 1 ? (
            <div className="space-y-2">
              <FieldHint
                label={`Lane directions (${singleStripLaneCount ?? 1} lane${(singleStripLaneCount ?? 1) === 1 ? '' : 's'})`}
                text={ROAD_FLOW_HINTS.laneDirections}
              />
              {renderCarriagewayLaneDirections(
                '',
                roadLaneDirections,
                onRoadLaneDirectionsChange,
                singleStripLaneCount,
              )}
              {renderPerLaneWidthInputs(
                '',
                singleStripLaneCount,
                roadLaneWidthsM,
                onRoadLaneWidthsMChange,
              )}
            </div>
            ) : null}
          </div>
          )
          ) : null}

          {roadFlowValidationError ? (
            <p className="text-xs text-red-600 dark:text-red-400">{roadFlowValidationError}</p>
          ) : null}
          {supportsSideFacilityTags(selectedCategory) && (
            guidedRoad ? (
            <RoadDetailsAccordionSection id="road-kerb" title="D. Kerbside facilities" badge="Optional">
              {renderKerbsideFacilitiesContent()}
            </RoadDetailsAccordionSection>
            ) : (
            <div className="space-y-3 rounded-xl border border-emerald-200/80 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-950/20 p-3">
              {renderKerbsideFacilitiesContent()}
            </div>
            )
          )}

          {guidedRoad && supportsSideFacilityTags(selectedCategory) ? (
            <RoadDetailsAccordionSection id="road-access" title="E. Carriageway access" badge="Optional">
              {renderCarriagewayAccessContent()}
            </RoadDetailsAccordionSection>
          ) : null}

          {guidedRoad ? (
          <RoadDetailsAccordionSection id="road-surface" title="F. Surface & condition" badge="Optional">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Road surface</label>
                <select value={roadSurface} onChange={(e) => onRoadSurfaceChange(e.target.value)} className={inputClass}>
                  <option value="">Select surface (optional)</option>
                  <option value="asphalt">Asphalt</option>
                  <option value="concrete">Concrete</option>
                  <option value="paved">Paved (other)</option>
                  <option value="unpaved">Unpaved</option>
                  <option value="gravel">Gravel / Compacted</option>
                  <option value="earth">Earth / Dirt</option>
                  <option value="mud">Mud</option>
                  <option value="sand">Sand</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Road quality / condition</label>
                <select value={roadSmoothness} onChange={(e) => onRoadSmoothnessChange(e.target.value)} className={inputClass}>
                  <option value="">Select quality (optional)</option>
                  <option value="excellent">Excellent — new / perfect surface</option>
                  <option value="good">Good — minor wear, comfortable</option>
                  <option value="intermediate">Intermediate — noticeable imperfections</option>
                  <option value="bad">Bad — significant roughness / potholes</option>
                  <option value="very_bad">Very bad — major damage, slow driving</option>
                  <option value="horrible">Horrible — barely passable</option>
                  <option value="very_horrible">Very horrible — extreme damage</option>
                  <option value="impassable">Impassable — road unusable</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
            <label className={labelClass}>Road Attributes</label>
            <div className="grid grid-cols-2 gap-2">
              {([
                { key: 'toll', label: 'Toll road', value: roadToll, setter: onRoadTollChange },
                { key: 'lit', label: 'Street lighting', value: roadLit, setter: onRoadLitChange },
                { key: 'flood_prone', label: 'Flood-prone', value: roadFloodProne, setter: onRoadFloodProneChange },
                { key: 'potholes', label: 'Potholes', value: roadPotholes, setter: onRoadPotholesChange },
                { key: 'drainage', label: 'Has drainage', value: roadDrainage, setter: onRoadDrainageChange },
                { key: 'street_vendors', label: 'Street vendors', value: roadStreetVendors, setter: onRoadStreetVendorsChange },
                { key: 'accident_prone', label: 'Accident-prone', value: roadAccidentProne, setter: onRoadAccidentProneChange },
                { key: 'hawkers', label: 'Hawkers', value: roadHawkers, setter: onRoadHawkersChange },
              ] as const).map((attr) => (
                <label key={attr.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-neutral-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attr.value}
                    onChange={(e) => attr.setter(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500"
                  />
                  {attr.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Speed Limit (km/h) — optional</label>
            <input
              type="number"
              min="5"
              max="130"
              step="5"
              value={roadMaxspeed}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || (parseInt(v, 10) >= 5 && parseInt(v, 10) <= 130)) onRoadMaxspeedChange(v);
              }}
              placeholder="e.g., 50"
              className={inputClass}
            />
            <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">Enter the posted speed limit if known.</p>
          </div>
          </RoadDetailsAccordionSection>
          ) : (
          <>
          <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/60 dark:bg-neutral-800/30 p-3">
            <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Surface &amp; condition</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Road surface</label>
                <select value={roadSurface} onChange={(e) => onRoadSurfaceChange(e.target.value)} className={inputClass}>
                  <option value="">Select surface (optional)</option>
                  <option value="asphalt">Asphalt</option>
                  <option value="concrete">Concrete</option>
                  <option value="paved">Paved (other)</option>
                  <option value="unpaved">Unpaved</option>
                  <option value="gravel">Gravel / Compacted</option>
                  <option value="earth">Earth / Dirt</option>
                  <option value="mud">Mud</option>
                  <option value="sand">Sand</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Road quality / condition</label>
                <select value={roadSmoothness} onChange={(e) => onRoadSmoothnessChange(e.target.value)} className={inputClass}>
                  <option value="">Select quality (optional)</option>
                  <option value="excellent">Excellent — new / perfect surface</option>
                  <option value="good">Good — minor wear, comfortable</option>
                  <option value="intermediate">Intermediate — noticeable imperfections</option>
                  <option value="bad">Bad — significant roughness / potholes</option>
                  <option value="very_bad">Very bad — major damage, slow driving</option>
                  <option value="horrible">Horrible — barely passable</option>
                  <option value="very_horrible">Very horrible — extreme damage</option>
                  <option value="impassable">Impassable — road unusable</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>Road Attributes</label>
            <div className="grid grid-cols-2 gap-2">
              {([
                { key: 'toll', label: 'Toll road', value: roadToll, setter: onRoadTollChange },
                { key: 'lit', label: 'Street lighting', value: roadLit, setter: onRoadLitChange },
                { key: 'flood_prone', label: 'Flood-prone', value: roadFloodProne, setter: onRoadFloodProneChange },
                { key: 'potholes', label: 'Potholes', value: roadPotholes, setter: onRoadPotholesChange },
                { key: 'drainage', label: 'Has drainage', value: roadDrainage, setter: onRoadDrainageChange },
                { key: 'street_vendors', label: 'Street vendors', value: roadStreetVendors, setter: onRoadStreetVendorsChange },
                { key: 'accident_prone', label: 'Accident-prone', value: roadAccidentProne, setter: onRoadAccidentProneChange },
                { key: 'hawkers', label: 'Hawkers', value: roadHawkers, setter: onRoadHawkersChange },
              ] as const).map((attr) => (
                <label key={attr.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-neutral-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attr.value}
                    onChange={(e) => attr.setter(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500"
                  />
                  {attr.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Speed Limit (km/h) — optional</label>
            <input
              type="number"
              min="5"
              max="130"
              step="5"
              value={roadMaxspeed}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || (parseInt(v, 10) >= 5 && parseInt(v, 10) <= 130)) onRoadMaxspeedChange(v);
              }}
              placeholder="e.g., 50"
              className={inputClass}
            />
            <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">Enter the posted speed limit if known.</p>
          </div>
          </>
          )}
          {(selectedCategory === 'pedestrian_crossing' || roadCrossingType) && (
            <div>
              <label className={labelClass}>Crossing type</label>
              <select value={roadCrossingType} onChange={(e) => onRoadCrossingTypeChange(e.target.value)} className={inputClass}>
                <option value="">Select crossing style (optional)</option>
                <option value="zebra">Zebra / marked</option>
                <option value="uncontrolled">Uncontrolled</option>
                <option value="unmarked">Unmarked</option>
                <option value="traffic_signals">Traffic signals</option>
                <option value="marked">Marked (other)</option>
              </select>
              <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">Used for map styling of pedestrian crossings.</p>
            </div>
          )}
        </div>
      )}

      {/* Transport stop-specific */}
      {ftId === 'transport_stop' && (
        <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 p-3">
          <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Stop details</p>
          <div>
            <label className={labelClass}>Stop reference / sign # (optional)</label>
            <input
              type="text"
              value={stopRef}
              onChange={(e) => onStopRefChange(e.target.value)}
              placeholder="e.g., Stop 12, ACC-45"
              className={inputClass}
            />
          </div>
          <RouteTagsInput
            label="Routes served (optional)"
            tags={stopRoutes}
            onChange={onStopRoutesChange}
            placeholder="e.g., Accra - Tema"
            hint="Press Enter or comma after each route. A stop often serves several routes."
            labelClass={labelClass}
          />
          <RouteTagsInput
            label="Operators (optional)"
            tags={stopOperators}
            onChange={onStopOperatorsChange}
            placeholder="e.g., VIP, Metro Mass"
            hint="Add each operator separately — some stops serve multiple operators."
            labelClass={labelClass}
          />
          <div className="flex flex-wrap gap-2">
            {TRANSIT_STOP_FACILITIES.map(({ key, label, Icon }) => {
              const value = key === 'shelter' ? stopShelter : key === 'bench' ? stopBench : stopLighting;
              const setter = key === 'shelter' ? onStopShelterChange : key === 'bench' ? onStopBenchChange : onStopLightingChange;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setter(!value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    value
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                      : 'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-neutral-400 hover:border-emerald-300'
                  }`}
                >
                  <Icon size={15} className={value ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showPoiExtras && (
        <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 p-3">
          <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">Business details (optional)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Cuisine / specialty</label>
              <input type="text" value={poiCuisine} onChange={(e) => onPoiCuisineChange(e.target.value)} placeholder="e.g., Ghanaian, Jollof" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Brand / chain</label>
              <input type="text" value={poiBrand} onChange={(e) => onPoiBrandChange(e.target.value)} placeholder="e.g., Shoprite, Goil" className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Wheelchair access</label>
            <select value={poiWheelchair} onChange={(e) => onPoiWheelchairChange(e.target.value)} className={inputClass}>
              <option value="">Not specified</option>
              <option value="yes">Yes — fully accessible</option>
              <option value="limited">Limited access</option>
              <option value="no">Not accessible</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={paymentMobileMoney} onChange={(e) => onPaymentMobileMoneyChange(e.target.checked)} className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 dark:bg-neutral-700 text-primary-600" />
              Mobile money accepted
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={paymentCard} onChange={(e) => onPaymentCardChange(e.target.checked)} className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 dark:bg-neutral-700 text-primary-600" />
              Card payments accepted
            </label>
          </div>
          {paymentMobileMoney && (
            <div>
              <label className={labelClass}>Mobile money provider</label>
              <input type="text" value={mobileMoneyProvider} onChange={(e) => onMobileMoneyProviderChange(e.target.value)} placeholder="e.g., MTN MoMo, Telecel Cash" className={inputClass} />
            </div>
          )}
        </div>
      )}

      {/* Tree-specific */}
      {ftId === 'tree' && (
        <div>
          <label className={labelClass}>Height in metres (optional)</label>
          <input
            type="number"
            min="0.5"
            max="80"
            step="0.5"
            value={treeHeight}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || (parseFloat(v) >= 0.5 && parseFloat(v) <= 80)) onTreeHeightChange(v);
            }}
            placeholder="e.g., 8"
            className={inputClass}
          />
          <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
            Helps reviewers and scales the 3D tree model when 3D objects are enabled.
          </p>
        </div>
      )}

      {/* Aeroway-specific */}
      {ftId === 'aeroway' && (
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Ref / code (optional)</label>
            <input
              type="text"
              value={aerowayRef}
              onChange={(e) => onAerowayRefChange(e.target.value)}
              placeholder="e.g., 21, A1, ACC"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Surface (optional)</label>
            <input
              type="text"
              value={aerowaySurface}
              onChange={(e) => onAerowaySurfaceChange(e.target.value)}
              placeholder="e.g., asphalt, concrete, grass"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Width in metres (optional)</label>
            <input
              type="number"
              min="1"
              max="200"
              step="0.5"
              value={aerowayWidth}
              onChange={(e) => onAerowayWidthChange(e.target.value)}
              placeholder="e.g., 45"
              className={inputClass}
            />
          </div>
        </div>
      )}

      {/* Motorway junction-specific */}
      {ftId === 'motorway_junction' && (
        <div className="space-y-3">
          <div className="rounded-xl border border-blue-200/80 bg-blue-50 px-3 py-2.5 text-xs text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/30 dark:text-blue-100">
            <p className="font-semibold">Where to place the pin</p>
            <p className="mt-1">
              Put the marker on the motorway exit ramp or the start of the slip road where traffic leaves the main carriageway — not in the middle of the motorway and not on the destination street itself.
            </p>
          </div>
          <div>
            <label className={labelClass}>Junction number / ref (optional)</label>
            <input
              type="text"
              value={junctionRef}
              onChange={(e) => onJunctionRefChange(e.target.value)}
              placeholder="e.g., 12, 5A"
              className={inputClass}
            />
          </div>
          <SearchableRoadMultiSelect
            label="Exit to (optional)"
            value={junctionExitRoads}
            onChange={onJunctionExitRoadsChange}
            proximity={junctionSearchProximity}
            placeholder="Search connecting road name…"
            hint="Search for roads you can reach from this exit. Add more than one if the junction serves multiple destinations."
            inputClass={inputClass}
            labelClass={labelClass}
          />
          {junctionSearchProximity && (
            <MotorwayLinkStatusChip
              lat={junctionSearchProximity.lat}
              lng={junctionSearchProximity.lng}
            />
          )}
        </div>
      )}

      {ftId === 'turn_restriction' && onTurnFromRoadChange && onTurnToRoadChange && onTurnRestrictionTypeChange && (
        <TurnRestrictionSurveyFields
          fromRoad={turnFromRoad}
          onFromRoadChange={onTurnFromRoadChange}
          toRoad={turnToRoad}
          onToRoadChange={onTurnToRoadChange}
          restrictionType={turnRestrictionType}
          onRestrictionTypeChange={onTurnRestrictionTypeChange}
          proximity={turnSearchProximity}
          labelClass={labelClass}
        />
      )}

      {/* Description */}
      {showDescription && (
      <>
      <div>
        <label className={labelClass}>Description{metadataOnly && metadataUpdateIntent === 'description' ? ' *' : ''}</label>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={
            (ftId === 'place' && selectedCategory && descPlaceholderMap[selectedCategory])
            || descPlaceholderMap[ftId]
            || 'Describe this place — what it looks like, what it is used for, any helpful details'
          }
          rows={3}
          className={`${inputClass} resize-none`}
        />
        <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
          Describe the place itself — this helps reviewers verify your contribution.
        </p>
      </div>

      <div>
        <label className={labelClass}>Description source (optional)</label>
        <input
          type="url"
          value={descriptionSourceUrl}
          onChange={(e) => onDescriptionSourceUrlChange(e.target.value)}
          placeholder="https://… where you found this description"
          className={inputClass}
        />
        <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
          Link to a website, article, or listing reviewers can open for context.
        </p>
      </div>
      </>
      )}

      {/* Photos */}
      {showPhotos && (
      <div>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          capture="environment"
          multiple
          className="sr-only"
          disabled={atMax}
          onFocus={() => console.log('[photo] input focused — file picker should open')}
          onChange={(e) => {
            console.log('[photo] input onChange — files:', e.target.files?.length ?? 0);
            onPhotoInputChange(e);
          }}
        />
        <div
          role="button"
          tabIndex={atMax ? -1 : 0}
          onClick={openPicker}
          onFocus={() => !atMax && setIsPasteReady(true)}
          onBlur={() => setIsPasteReady(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
          onDragOver={handlePhotoDragOver}
          onDragEnter={handlePhotoDragOver}
          onDragLeave={handlePhotoDragLeave}
          onDrop={handlePhotoDrop}
          onPaste={handlePhotoPaste}
          className={`w-full flex flex-col items-center justify-center gap-1 py-4 border-2 border-dashed rounded-xl transition-colors select-none ${atMax
            ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-neutral-600 text-gray-400 dark:text-neutral-500'
            : isDragOver
              ? 'border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-950/30 text-primary-600 dark:text-primary-400 cursor-copy'
              : isPasteReady
                ? 'border-primary-400 dark:border-primary-500 bg-primary-50/50 dark:bg-primary-950/20 text-primary-600 dark:text-primary-400 cursor-pointer'
                : 'border-gray-300 dark:border-neutral-600 text-gray-600 dark:text-neutral-300 cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400'
            }`}
        >
          <Camera size={20} />
          <span className="text-sm font-medium">
            {atMax ? `Maximum ${MAX_CONTRIBUTION_PHOTOS} photos` : isDragOver ? 'Drop to add' : 'Add photos (optional)'}
          </span>
          {!atMax && (
            <span className="text-xs text-gray-400 dark:text-neutral-500 text-center px-4">
              Take a photo, browse your gallery, drag and drop, or paste (Ctrl+V / Cmd+V)
            </span>
          )}
        </div>
        {photoDataUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {photoDataUrls.map((src, i) => (
              <div key={`${i}-${src.slice(0, 32)}`} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-neutral-600">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => onRemovePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs flex items-center justify-center hover:bg-black/80"
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={
          submitLoading
          || (!metadataOnly && (isFullDetails || ftId === 'place') && !name.trim())
          || (!metadataOnly && ftId === 'place' && !selectedCategory)
        }
        className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed shadow-md"
      >
        {submitLoading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" />
            Checking…
          </span>
        ) : (
          submitLabel ?? (ftId === 'building' ? 'Next: Building Units' : 'Review & Submit')
        )}
      </button>
    </div>
  );
}
